
import Table from "../../components/Table";
import { useAuth } from "../../context/AuthContext";
import AddDialog from "../../components/Modal/AddModal";
import MultiSelectInput from "../../components/Select/MultiSelectInput";
import SelectInput from "../../components/Select/SelectInput";
import TextInput from "../../components/Select/InputText";
import { getPackages } from "../../services/packagesService";
import { deleteSubscription, getSubscriptions, notifySubscription, renewService, SubscriptionScope } from "../../services/renewService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { hasPermission } from "../../utils/permissions";
import { FaRedo } from "react-icons/fa";

type SubscriptionRow = {
    _id: string;
    member?: {
        _id?: string;
        id?: string;
        firstName?: string;
        lastName?: string;
    } | string;
    package?: {
        name?: string;
    };
    startDate?: string;
    expirationDate?: string;
    status?: boolean;
    notify?: boolean;
    price?: number;
};

type SubscriptionPageState = {
    rows: SubscriptionRow[];
    page: number;
    total: number;
    loading: boolean;
};

const PAGE_SIZE = 10;

const emptyPageState: SubscriptionPageState = {
    rows: [],
    page: 1,
    total: 0,
    loading: false,
};

const readRows = (response: unknown): SubscriptionRow[] => {
    if (Array.isArray(response)) return response as SubscriptionRow[];
    if (!response || typeof response !== "object") return [];

    const payload = response as Record<string, unknown>;
    if (Array.isArray(payload.data)) return payload.data as SubscriptionRow[];
    if (Array.isArray(payload.subscriptions)) return payload.subscriptions as SubscriptionRow[];
    if (Array.isArray(payload.items)) return payload.items as SubscriptionRow[];

    return [];
};

const readTotal = (response: unknown, fallback: number): number => {
    if (!response || typeof response !== "object") return fallback;

    const payload = response as Record<string, unknown>;
    const pagination = payload.pagination && typeof payload.pagination === "object"
        ? payload.pagination as Record<string, unknown>
        : {};
    const total = payload.total ?? payload.totalCount ?? pagination.total ?? pagination.totalCount ?? payload.count;

    if (typeof total === "number" && Number.isFinite(total)) return total;
    if (typeof total === "string") {
        const parsed = Number(total);
        if (Number.isFinite(parsed)) return parsed;
    }

    return fallback;
};

const formatDateInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getLastMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);

    return {
        startDate: formatDateInput(start),
        endDate: formatDateInput(end),
    };
};

type SubscriptionsProps = {
    subscriptionType?: "normal" | "private";
    showViewAll?: boolean;
};

const Subscriptions = ({ subscriptionType = "normal", showViewAll = true }: SubscriptionsProps) => {
    const { user } = useAuth();
    const canEditSubscriptions = hasPermission(user, "subscriptions.write");
    const [thisMonth, setThisMonth] = useState<SubscriptionPageState>(emptyPageState);
    const [oldSubscriptions, setOldSubscriptions] = useState<SubscriptionPageState>(emptyPageState);
    const [search, setSearch] = useState("");
    const [notifyingId, setNotifyingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [renewOpen, setRenewOpen] = useState(false);
    const [renewingMemberId, setRenewingMemberId] = useState("");
    const [selectedPackage, setSelectedPackage] = useState("");
    const [packageFilter, setPackageFilter] = useState<string[]>([]);
    const [startDate, setStartDate] = useState("");
    const [oldStartDate, setOldStartDate] = useState(() => getLastMonthRange().startDate);
    const [oldEndDate, setOldEndDate] = useState(() => getLastMonthRange().endDate);
    const [packages, setPackages] = useState<any[]>([]);

    const formatDate = (value?: string) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toISOString().split("T")[0];
    };

    const formatMemberName = (row?: SubscriptionRow) => {
        if (typeof row?.member === "string") return row.member;
        const firstName = row?.member?.firstName ?? "";
        const lastName = row?.member?.lastName ?? "";
        return [firstName, lastName].filter(Boolean).join(" ") || "-";
    };

    const getMemberId = (row?: SubscriptionRow) => {
        if (typeof row?.member === "string") return row.member;
        return row?.member?._id ?? row?.member?.id ?? "";
    };

    const selectedPackageDetails = packages.find((pkg) => pkg?._id === selectedPackage);
    const packageOptions = packages.map((pkg) => ({
        value: pkg._id,
        label: pkg.name,
    }));
    const packageFilterQuery = packageFilter.length
        ? `&${packageFilter.map((id) => `packageId=${encodeURIComponent(id)}`).join("&")}`
        : "";

    const updateNotifiedRow = (subscriptionId: string) => {
        const markRow = (row: SubscriptionRow) =>
            row?._id === subscriptionId ? { ...row, notify: true } : row;

        setThisMonth((prev) => ({ ...prev, rows: prev.rows.map(markRow) }));
        setOldSubscriptions((prev) => ({ ...prev, rows: prev.rows.map(markRow) }));
    };

    const removeDeletedRow = (subscriptionId: string) => {
        const removeRow = (row: SubscriptionRow) => row?._id !== subscriptionId;
        setThisMonth((prev) => ({
            ...prev,
            rows: prev.rows.filter(removeRow),
            total: Math.max(prev.total - 1, 0),
        }));
        setOldSubscriptions((prev) => ({
            ...prev,
            rows: prev.rows.filter(removeRow),
            total: Math.max(prev.total - 1, 0),
        }));
    };

    const handleNotify = async (subscriptionId: string) => {
        try {
            setNotifyingId(subscriptionId);
            await notifySubscription(subscriptionId, true);
            updateNotifiedRow(subscriptionId);
        } catch (err) {
            console.error("Failed to send notification", err);
            showErrorToast("Failed to notify member");
        } finally {
            setNotifyingId(null);
        }
    };

    const handleDelete = async (subscriptionId: string) => {
        if (!subscriptionId) return;
        const confirmed = window.confirm("Delete this subscription?");
        if (!confirmed) return;

        try {
            setDeletingId(subscriptionId);
            await deleteSubscription(subscriptionId);
            removeDeletedRow(subscriptionId);
            showSuccessToast("Subscription deleted");
        } catch (err) {
            console.error("Failed to delete subscription", err);
            showErrorToast("Failed to delete subscription");
        } finally {
            setDeletingId(null);
        }
    };

    const fetchPage = async (
        scope: SubscriptionScope,
        page: number,
        search: string,
        setState: React.Dispatch<React.SetStateAction<SubscriptionPageState>>
    ) => {
        if (!user?.id) return;

        try {
            setState((prev) => ({ ...prev, loading: true }));
            const response = await getSubscriptions({
                scope,
                page,
                limit: PAGE_SIZE,
                search,
                type: subscriptionType,
                packageId: packageFilter,
                startDate: scope === "old" ? oldStartDate : undefined,
                endDate: scope === "old" ? oldEndDate : undefined,
            });
            const rows = readRows(response);
            setState({
                rows,
                page,
                total: readTotal(response, rows.length),
                loading: false,
            });
        } catch (err) {
            console.error(`Failed to fetch ${scope} subscriptions`, err);
            showErrorToast("Failed to fetch subscriptions");
            setState((prev) => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        if (!user?.id) return;
        fetchPage("thisMonth", 1, search, setThisMonth);
    }, [user?.id, search, subscriptionType, packageFilter]);

    useEffect(() => {
        if (!user?.id) return;
        fetchPage("old", 1, search, setOldSubscriptions);
    }, [user?.id, search, subscriptionType, packageFilter, oldStartDate, oldEndDate]);

    useEffect(() => {
        if (!user?.id) return;

        const fetchPackages = async () => {
            try {
                const response = await getPackages();
                setPackages(Array.isArray(response) ? response : []);
            } catch (err) {
                console.error("Failed to fetch packages", err);
                showErrorToast("Failed to fetch packages");
            }
        };

        fetchPackages();
    }, [user?.id]);

    const openRenewDialog = (row: SubscriptionRow) => {
        const memberId = getMemberId(row);
        if (!memberId) {
            showErrorToast("No member found for this subscription");
            return;
        }

        setRenewingMemberId(memberId);
        setSelectedPackage("");
        setStartDate(new Date().toISOString().split("T")[0]);
        setRenewOpen(true);
    };

    const closeRenewDialog = () => {
        setRenewOpen(false);
        setRenewingMemberId("");
        setSelectedPackage("");
        setStartDate("");
    };

    const refreshCurrentPages = async () => {
        await Promise.all([
            fetchPage("thisMonth", thisMonth.page, search, setThisMonth),
            fetchPage("old", oldSubscriptions.page, search, setOldSubscriptions),
        ]);
    };

    const handleRenewMembership = async () => {
        if (!user?.id) {
            showErrorToast("User not logged in");
            return;
        }
        if (!renewingMemberId) {
            showErrorToast("No member selected");
            return;
        }
        if (!selectedPackage) {
            showErrorToast("Please select a package");
            return;
        }
        if (!startDate) {
            showErrorToast("Please choose a start date");
            return;
        }

        try {
            await renewService({
                memberId: renewingMemberId,
                packageId: selectedPackage,
                startDate,
            });
            showSuccessToast("Membership renewed successfully");
            closeRenewDialog();
            await refreshCurrentPages();
        } catch (err) {
            console.error("Renewal failed", err);
            showErrorToast((err as any)?.response?.data?.error || "Failed to renew membership");
        }
    };

    const StatusCell = ({ value }: { value: boolean }) => (
        <span className={`px-2 py-1 rounded-md ${value ? "text-green-500" : "text-red-500"}`}>
            {value ? "Active" : "Inactive"}
        </span>
    );

    const NotifyCell = ({ value }: { value?: boolean }) => (
        <span className={`px-2 py-1 rounded-md ${value ? "text-green-600" : "text-gray-600"}`}>
            {value ? "Yes" : "No"}
        </span>
    );

    const columns = [
        {
            Header: "Member",
            accessor: "member",
            Cell: ({ row }: { row: { original: SubscriptionRow } }) => formatMemberName(row.original),
        },
        {
            Header: "Package",
            accessor: "package",
            Cell: ({ row }: { row: { original: SubscriptionRow } }) => row.original.package?.name ?? "-",
        },
        { Header: "Start Date", accessor: "startDate", Cell: ({ value }: { value?: string }) => formatDate(value) },
        { Header: "End Date", accessor: "expirationDate", Cell: ({ value }: { value?: string }) => formatDate(value) },
        { Header: "Status", accessor: "status", Cell: StatusCell },
        { Header: "Notify", accessor: "notify", Cell: NotifyCell },
        { Header: "Price", accessor: "price" },
        ...(canEditSubscriptions
            ? [{
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }: { row: { original: SubscriptionRow } }) => {
                const subscriptionId = row?.original?._id;
                const alreadyNotified = Boolean(row?.original?.notify);
                const isNotifying = notifyingId === subscriptionId;
                const isDeleting = deletingId === subscriptionId;

                return (
                    <div className="flex gap-2">
	                        <button
	                            type="button"
	                            onClick={() => openRenewDialog(row.original)}
	                            disabled={!subscriptionId || isDeleting || isNotifying}
	                            title="Renew membership"
	                            className="rounded-md bg-[#ac120c] px-2 py-1 text-white transition hover:bg-[#90100b] disabled:cursor-not-allowed disabled:opacity-50"
	                        >
	                            <FaRedo />
	                        </button>
	                        <button
	                            type="button"
	                            onClick={() => handleNotify(subscriptionId)}
                            disabled={!subscriptionId || alreadyNotified || isNotifying || isDeleting}
                            className="px-3 py-1 rounded-md bg-[#ac120c] hover:bg-[#90100b] transition text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {alreadyNotified ? "Notified" : isNotifying ? "Sending..." : "Notify"}
                        </button>
                        <button
                            type="button"
                            onClick={() => handleDelete(subscriptionId)}
                            disabled={!subscriptionId || isDeleting || isNotifying}
                            className="px-3 py-1 rounded-md bg-gray-800 hover:bg-black transition text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                    </div>
                );
                },
            }]
            : []),
    ];

    const renderPagination = (
        state: SubscriptionPageState,
        scope: SubscriptionScope,
        setState: React.Dispatch<React.SetStateAction<SubscriptionPageState>>
    ) => {
        const totalPages = Math.max(Math.ceil(state.total / PAGE_SIZE), 1);

        return (
            <div className="mt-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => fetchPage(scope, state.page - 1, search, setState)}
                    disabled={state.loading || state.page <= 1}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Previous
                </button>
                <span className="text-sm">
                    Page {state.page} of {totalPages}
                </span>
                <button
                    type="button"
                    onClick={() => fetchPage(scope, state.page + 1, search, setState)}
                    disabled={state.loading || state.page >= totalPages}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        );
    };

    return (
        <div className="w-full space-y-8">
            <section>
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">This Month Subscriptions</h2>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                            <span>Total: {thisMonth.total.toLocaleString()}</span>
                            {thisMonth.loading && <span>Loading...</span>}
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <MultiSelectInput
                            label="Packages"
                            name="packageFilter"
                            value={packageFilter}
                            options={packageOptions}
                            placeholder="All packages"
                            onChange={setPackageFilter}
                        />
                        <input
                            type="search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search subscriptions..."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm sm:w-64"
                        />
                        {showViewAll && (
                            <Link
                                to={`/subscriptions/all?scope=thisMonth${packageFilterQuery}`}
                                className="rounded-md bg-[#ac120c] px-4 py-2 text-center text-sm font-semibold text-white hover:bg-[#90100b]"
                            >
                                View All
                            </Link>
                        )}
                    </div>
                </div>
                <Table
                    columns={columns}
                    data={thisMonth.rows}
                    pageSize={PAGE_SIZE}
                    add={false}
                    renew={false}
                    checkbox={true}
                    showSearch={false}
                    showPagination={false}
                />
                {renderPagination(thisMonth, "thisMonth", setThisMonth)}
            </section>

            <section>
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">Old Subscriptions</h2>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                            <span>Total: {oldSubscriptions.total.toLocaleString()}</span>
                            {oldSubscriptions.loading && <span>Loading...</span>}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <TextInput
                            label="Start Date"
                            type="date"
                            name="oldStartDate"
                            value={oldStartDate}
                            onChange={(event) => setOldStartDate(event.target.value)}
                        />
                        <TextInput
                            label="End Date"
                            type="date"
                            name="oldEndDate"
                            value={oldEndDate}
                            onChange={(event) => setOldEndDate(event.target.value)}
                        />
                    </div>
                </div>
                <Table
                    columns={columns}
                    data={oldSubscriptions.rows}
                    pageSize={PAGE_SIZE}
                    add={false}
                    renew={false}
                    checkbox={true}
                    showSearch={false}
                    showPagination={false}
                />
                {renderPagination(oldSubscriptions, "old", setOldSubscriptions)}
            </section>

            <AddDialog title="Renew Membership" isOpen={renewOpen} onClose={closeRenewDialog}>
                <div className="p-6">
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            handleRenewMembership();
                        }}
                        className="flex flex-col gap-4"
                    >
                        <SelectInput
                            label="Packages"
                            name="package"
                            value={selectedPackage}
                            onChange={(event) => setSelectedPackage(event.target.value)}
                            options={packages.map((pkg) => ({
                                value: pkg._id,
                                label: pkg.name,
                            }))}
                        />

                        {selectedPackageDetails && (
                            <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                                <p className="mb-1 font-semibold">{selectedPackageDetails.name}</p>
                                <p>
                                    Duration:{" "}
                                    <span className="font-medium">
                                        {selectedPackageDetails.durationValue} {selectedPackageDetails.durationUnit}
                                        {selectedPackageDetails.durationValue > 1 ? "s" : ""}
                                    </span>
                                </p>
                                <p>
                                    Price: <span className="font-medium">{selectedPackageDetails.price} $</span>
                                </p>
                                {Array.isArray(selectedPackageDetails.revenueAllocations)
                                    && selectedPackageDetails.revenueAllocations.length > 0 && (
                                    <div className="mt-2 border-t border-gray-200 pt-2">
                                        <p className="font-semibold">Subscription revenue split</p>
                                        {selectedPackageDetails.revenueAllocations.map((allocation: any) => (
                                            <p key={allocation._id}>
                                                {allocation.name}: <span className="font-medium">{allocation.percentage}%</span>
                                            </p>
                                        ))}
                                        <p className="mt-1 text-xs text-gray-500">
                                            Coach commission and salary amounts will be frozen when this subscription is created.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <TextInput
                            label="Start Date"
                            type="date"
                            name="startDate"
                            value={startDate}
                            onChange={(event) => setStartDate(event.target.value)}
                        />

                        <button type="submit" className="rounded bg-blue-500 px-4 py-2 text-white">
                            Renew Membership
                        </button>
                    </form>
                </div>
            </AddDialog>
        </div>
    );
};

export default Subscriptions;
